import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';

// ==================== ADMIN MANAGEMENT ====================

/**
 * Get all admin emails from Firestore
 * @returns {Promise<Array<{email: string, addedBy: string, addedAt: any}>>} Array of admin objects
 */
export const getAdmins = async () => {
  try {
    const adminsRef = collection(db, 'admins');
    const snapshot = await getDocs(adminsRef);
    return snapshot.docs.map(doc => ({
      email: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching admins:', error);
    return [];
  }
};

/**
 * Check if an email is an admin
 * @param {string} email - Email address to check
 * @returns {Promise<boolean>} True if user is admin
 */
export const isAdmin = async (email) => {
  if (!email) return false;

  try {
    const adminDoc = doc(db, 'admins', email.toLowerCase());
    const snapshot = await getDoc(adminDoc);
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Add a new admin
 * @param {string} email - Email address to add as admin
 * @param {string} addedBy - Email of admin who is adding this user
 * @returns {Promise<void>}
 */
export const addAdmin = async (email, addedBy = 'system') => {
  try {
    const adminDoc = doc(db, 'admins', email.toLowerCase());
    await setDoc(adminDoc, {
      email: email.toLowerCase(),
      role: 'admin',
      addedAt: serverTimestamp(),
      addedBy: addedBy
    });
    console.log('Admin added:', email);
  } catch (error) {
    console.error('Error adding admin:', error);
    throw error;
  }
};

/**
 * Remove an admin
 * @param {string} email - Email address to remove
 * @returns {Promise<void>}
 */
export const removeAdmin = async (email) => {
  try {
    const adminDoc = doc(db, 'admins', email.toLowerCase());
    await deleteDoc(adminDoc);
    console.log('Admin removed:', email);
  } catch (error) {
    console.error('Error removing admin:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time admin list updates
 * @param {Function} callback - Function to call with updated admin list
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAdmins = (callback) => {
  const adminsRef = collection(db, 'admins');
  return onSnapshot(adminsRef, (snapshot) => {
    const emails = snapshot.docs.map(doc => doc.id);
    callback(emails);
  }, (error) => {
    console.error('Error in admins subscription:', error);
    callback([]);
  });
};

// ==================== ALBUM RESTRICTIONS ====================

/**
 * Get album restriction settings
 * @param {string} albumId - Album ID
 * @returns {Promise<Object>} Album restriction data {isRestricted, allowedEmails}
 */
export const getAlbumRestrictions = async (albumId) => {
  try {
    const albumDoc = doc(db, 'albums', albumId);
    const snapshot = await getDoc(albumDoc);

    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        isRestricted: data.isRestricted || false,
        allowedEmails: data.allowedEmails || []
      };
    }

    // Default to public if no restrictions set
    return { isRestricted: false, allowedEmails: [] };
  } catch (error) {
    console.error('Error fetching album restrictions:', error);
    return { isRestricted: false, allowedEmails: [] };
  }
};

/**
 * Set album restriction settings
 * @param {string} albumId - Album ID
 * @param {boolean} isRestricted - Whether album is restricted
 * @param {string[]} allowedEmails - Array of allowed email addresses (empty = public)
 * @returns {Promise<void>}
 */
export const setAlbumRestrictions = async (albumId, isRestricted, allowedEmails = []) => {
  try {
    const albumDoc = doc(db, 'albums', albumId);
    await setDoc(albumDoc, {
      isRestricted: isRestricted,
      allowedEmails: allowedEmails.map(email => email.toLowerCase()),
      updatedAt: serverTimestamp()
    }, { merge: true }); // Merge to preserve other album data

    console.log('Album restrictions updated:', albumId);
  } catch (error) {
    console.error('Error setting album restrictions:', error);
    throw error;
  }
};

/**
 * Check if a user can access an album
 * @param {string} albumId - Album ID
 * @param {string} userEmail - User's email address
 * @returns {Promise<boolean>} True if user can access album
 */
export const canAccessAlbum = async (albumId, userEmail) => {
  try {
    const restrictions = await getAlbumRestrictions(albumId);

    // If not restricted, everyone can access
    if (!restrictions.isRestricted) {
      return true;
    }

    // If restricted, check if user is in allowed list
    if (!userEmail) {
      return false;
    }

    return restrictions.allowedEmails.includes(userEmail.toLowerCase());
  } catch (error) {
    console.error('Error checking album access:', error);
    // Default to deny access on error
    return false;
  }
};

/**
 * Get all albums the user can access
 * @param {string} userEmail - User's email address (null for anonymous)
 * @returns {Promise<string[]>} Array of accessible album IDs
 */
export const getAccessibleAlbums = async (userEmail) => {
  try {
    const albumsRef = collection(db, 'albums');
    const snapshot = await getDocs(albumsRef);

    const accessible = [];

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const albumId = docSnap.id;

      // Include if not restricted
      if (!data.isRestricted) {
        accessible.push(albumId);
        continue;
      }

      // Include if user is in allowed list
      if (userEmail && data.allowedEmails?.includes(userEmail.toLowerCase())) {
        accessible.push(albumId);
      }
    }

    return accessible;
  } catch (error) {
    console.error('Error getting accessible albums:', error);
    return [];
  }
};

/**
 * Store full album metadata in Firestore (optional - can be used alongside Storage)
 * @param {string} albumId - Album ID
 * @param {Object} metadata - Album metadata
 * @returns {Promise<void>}
 */
export const setAlbumMetadata = async (albumId, metadata) => {
  try {
    const albumDoc = doc(db, 'albums', albumId);
    await setDoc(albumDoc, {
      ...metadata,
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log('Album metadata stored in Firestore:', albumId);
  } catch (error) {
    console.error('Error storing album metadata:', error);
    throw error;
  }
};

/**
 * Get album metadata from Firestore
 * @param {string} albumId - Album ID
 * @returns {Promise<Object|null>} Album metadata or null if not found
 */
export const getAlbumMetadata = async (albumId) => {
  try {
    const albumDoc = doc(db, 'albums', albumId);
    const snapshot = await getDoc(albumDoc);

    if (snapshot.exists()) {
      return { id: albumId, ...snapshot.data() };
    }

    return null;
  } catch (error) {
    console.error('Error fetching album metadata:', error);
    return null;
  }
};

// ==================== SITE CONFIGURATION ====================

/**
 * Get site configuration (title, description, etc.)
 * @returns {Promise<Object>} Site configuration object
 */
export const getSiteConfig = async () => {
  try {
    const configDoc = doc(db, 'config', 'site');
    const snapshot = await getDoc(configDoc);

    if (snapshot.exists()) {
      return snapshot.data();
    }

    // Return defaults if config doesn't exist
    return {
      title: 'CHS Media',
      shortName: 'CHS Media',
      description: 'Photo gallery and videos',
      showVideosButton: true,
      uploadPin: '202526',
      uploadPinUpdatedAt: null
    };
  } catch (error) {
    console.error('Error fetching site config:', error);
    // Return defaults on error
    return {
      title: 'CHS Media',
      shortName: 'CHS Media',
      description: 'Photo gallery and videos',
      showVideosButton: true,
      uploadPin: '202526',
      uploadPinUpdatedAt: null
    };
  }
};

/**
 * Set site configuration
 * @param {Object} config - Site configuration {title, shortName, description, uploadPin}
 * @returns {Promise<void>}
 */
export const setSiteConfig = async (config, previousConfig = null) => {
  try {
    const configDoc = doc(db, 'config', 'site');
    const updateData = {
      ...config,
      updatedAt: serverTimestamp()
    };

    // If uploadPin is being changed, update the timestamp
    if (config.uploadPin && previousConfig && config.uploadPin !== previousConfig.uploadPin) {
      updateData.uploadPinUpdatedAt = Date.now();
    } else if (config.uploadPin && !previousConfig) {
      // First time setting upload PIN
      updateData.uploadPinUpdatedAt = Date.now();
    }

    await setDoc(configDoc, updateData);

    console.log('Site config updated:', config);
  } catch (error) {
    console.error('Error setting site config:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time site config updates
 * @param {Function} callback - Function to call with updated config
 * @returns {Function} Unsubscribe function
 */
export const subscribeToSiteConfig = (callback) => {
  const configDoc = doc(db, 'config', 'site');
  return onSnapshot(configDoc, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data());
    } else {
      // Call with defaults if doesn't exist
      callback({
        title: 'CHS Media',
        shortName: 'CHS Media',
        description: 'Photo gallery and videos',
        showVideosButton: true,
        uploadPin: '202526',
        uploadPinUpdatedAt: null
      });
    }
  }, (error) => {
    console.error('Error in site config subscription:', error);
    callback({
      title: 'CHS Media',
      shortName: 'CHS Media',
      description: 'Photo gallery and videos',
      showVideosButton: true,
      uploadPin: '202526',
      uploadPinUpdatedAt: null
    });
  });
};
